import { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { RIDE_STATUSES, TERM_REASONS } from "score-common";
import { useAnnouncerContext } from "../context/AnnouncerContext";
import { useRingData } from "../context/RingDataContext";
import ClassResults from "./ClassResults";
import {
  isFollowingRing,
  computeReturnDot,
  classifyRideForLookup,
  describeLookupClassHeading,
  resolveClassSponsorship,
  RIDE_LOOKUP_KIND,
} from "../utils/lookupColumn";
import { buildClassResults } from "../utils/orderOfGo";
import { formatComposite } from "../utils/formatComposite";

// #2142 (docs/score/announcer-ui-redesign.md §4.4/§5) — band 4, the look-up
// column: the only band that scrolls, showing exactly one class at a time.
// Consumes ONLY useAnnouncerContext()/useRingData() plus ClassResults itself
// (the card's one authorized new data source, #2141's `useClassResults`
// hook lives inside ClassResults already) — no new poll of its own.

const HEADER_HEIGHT = 44;
const PLACINGS_HEIGHT = 220;

// #1838/LiveDesk.jsx precedent — same small local duplication rather than a
// shared helper across bands (daySpine's titleCaseWeekday is the same call).
function rideExceptionLabel(row, t) {
  if (!row) return null;
  if (row.termReason === TERM_REASONS.ELIMINATED) return t("liveDesk.eliminated");
  if (row.status === RIDE_STATUSES.SCRATCHED) return row.termReason || t("oog.scratched");
  return null;
}

function ScoreCell({ row, isCurrent, result, t }) {
  const { kind, composite } = classifyRideForLookup({ status: row.status, isCurrent, result });
  if (kind === RIDE_LOOKUP_KIND.SCRATCHED) return null;
  if (kind === RIDE_LOOKUP_KIND.DASH) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("results.unplaced")}
      </Typography>
    );
  }
  if (kind === RIDE_LOOKUP_KIND.PENDING) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("lookupColumn.pending")}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" data-testid="lookup-row-score">
      {t("results.composite", { value: formatComposite(composite) })}
    </Typography>
  );
}

function RideListRow({ row, isCurrent, result, t }) {
  const exception = rideExceptionLabel(row, t);
  const scratched = row.status === RIDE_STATUSES.SCRATCHED;
  return (
    <Box
      data-testid="lookup-ride-row"
      data-ride-id={row.id}
      data-current={isCurrent ? "true" : "false"}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderBottom: 1,
        borderColor: "divider",
        textDecoration: scratched ? "line-through" : "none",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
          {row.competitorNumber} {row.riderName}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {row.horseName}
        </Typography>
        {exception && (
          <Typography
            variant="caption"
            data-testid="lookup-row-exception"
            sx={{ color: colors.foxOrange, display: "block" }}
          >
            {exception}
          </Typography>
        )}
      </Box>
      <ScoreCell row={row} isCurrent={isCurrent} result={result} t={t} />
    </Box>
  );
}

export default function LookupColumn() {
  const { t } = useTranslation("announcer");
  const { viewingClassId, setViewingClassId, browserOnline } = useAnnouncerContext();
  const {
    classes,
    liveClassId,
    isClassPlacedFromSummary,
    rideRowsByClassId,
    resultsByClassId,
    handleClassResults,
    currentRideId,
    statusesUnknown,
  } = useRingData();

  const viewedClassId = viewingClassId ?? liveClassId;
  const following = isFollowingRing(viewedClassId, liveClassId);

  // AC9's dot: latch a snapshot of {liveClassId, placed} at the moment
  // `following` flips false, cleared when it flips back true, so the dot's
  // "while away" comparison never drifts with the live class's own churn
  // once the announcer has already returned.
  const leftSnapshotRef = useRef(null);
  const wasFollowingRef = useRef(following);
  useEffect(() => {
    if (wasFollowingRef.current && !following) {
      leftSnapshotRef.current = {
        liveClassId,
        placed: isClassPlacedFromSummary(liveClassId),
      };
    } else if (!wasFollowingRef.current && following) {
      leftSnapshotRef.current = null;
    }
    wasFollowingRef.current = following;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [following]);

  const returnDot = computeReturnDot({
    following,
    liveClassId,
    liveClassIdWhenLeft: leftSnapshotRef.current?.liveClassId ?? liveClassId,
    placedWhenLeft: leftSnapshotRef.current?.placed ?? false,
    placedNow: isClassPlacedFromSummary(liveClassId),
  });

  const liveClass = (classes || []).find((c) => c.id === liveClassId);
  const rideRows = rideRowsByClassId.get(viewedClassId) || [];
  const resultsMap = new Map(
    buildClassResults(resultsByClassId[viewedClassId] || [], rideRows).map((r) => [r.rideId, r]),
  );

  // #2584 (TRI-237) — the heading naming which class/test this panel is
  // showing. Derived from the VIEWED class (not the live one), so it tracks
  // whichever class the announcer is actually looking at, following or
  // pinned back.
  const { classLabel, testName } = describeLookupClassHeading({
    classes,
    classId: viewedClassId,
    rideRows,
  });

  // #2633 — the sponsor/prize for the class CURRENTLY IN THE RING (Jaybill's
  // description: "Show it for the class currently in the ring"), not
  // whichever class the announcer has pinned back to view — deliberately
  // liveClassId, not viewedClassId, unlike classLabel/testName above.
  const { sponsorName, sponsorTextLine, prize } = resolveClassSponsorship({
    classes,
    classId: liveClassId,
  });
  const hasSponsorship = Boolean(sponsorName || prize);

  return (
    <Box data-testid="lookup-column" sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        data-testid="lookup-column-header"
        data-following={following ? "true" : "false"}
        sx={{
          height: HEADER_HEIGHT,
          boxSizing: "border-box",
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          px: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {following ? (
          <Typography data-testid="lookup-following-label" variant="overline" color="text.secondary">
            {t("lookupColumn.followingTheRing")}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              data-testid="lookup-return-button"
              component="button"
              type="button"
              onClick={() => setViewingClassId(null)}
              variant="body2"
              sx={{
                cursor: "pointer",
                color: colors.foxOrange,
                background: "none",
                border: "none",
                p: 0,
                textAlign: "left",
                font: "inherit",
                fontWeight: 700,
              }}
            >
              {liveClass
                ? t("lookupColumn.backToClass", { classNumber: liveClass.classNumber })
                : t("lookupColumn.backToRing")}
            </Typography>
            {returnDot && (
              <Box
                data-testid="lookup-return-dot"
                sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colors.foxOrange }}
              />
            )}
          </Box>
        )}
      </Box>

      {classLabel && (
        <Box data-testid="lookup-class-heading" sx={{ px: 1.5, pt: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {testName
              ? t("lookupColumn.classHeadingWithTest", { classLabel, testName })
              : t("lookupColumn.classHeading", { classLabel })}
          </Typography>
        </Box>
      )}

      {hasSponsorship && (
        <Box data-testid="lookup-sponsorship" sx={{ px: 1.5, pt: 0.5 }}>
          {sponsorName && (
            <Typography data-testid="lookup-sponsor-name" variant="body2" sx={{ fontWeight: 600 }}>
              {t("lookupColumn.sponsorName", { name: sponsorName })}
            </Typography>
          )}
          {sponsorTextLine && (
            <Typography
              data-testid="lookup-sponsor-text-line"
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {t("lookupColumn.sponsorTextLine", { textLine: sponsorTextLine })}
            </Typography>
          )}
          {prize && (
            <Typography data-testid="lookup-prize" variant="body2" color="text.secondary">
              {t("lookupColumn.prize", { prize })}
            </Typography>
          )}
        </Box>
      )}

      <Box
        data-testid="lookup-placings"
        sx={{ height: PLACINGS_HEIGHT, flexShrink: 0, overflowY: "auto" }}
      >
        <ClassResults
          key={viewedClassId}
          classId={viewedClassId}
          isLive={viewedClassId === liveClassId}
          placed={isClassPlacedFromSummary(viewedClassId)}
          rideRows={rideRows}
          onResults={handleClassResults}
          online={browserOnline}
          statusesUnknown={statusesUnknown}
        />
      </Box>

      <Box data-testid="lookup-ride-list">
        {rideRows.map((row) => (
          <RideListRow
            key={row.id}
            row={row}
            isCurrent={row.id === currentRideId}
            result={resultsMap.get(row.id) || null}
            t={t}
          />
        ))}
      </Box>
    </Box>
  );
}
